import settings from "settings";
import ResponseFormatError from "shared/errors/responseFormatError";
import StandardApiError from "shared/errors/standardApiError";
import {
  ApiQueryResponseSchema,
  ApiMutationResponseSchema,
} from "shared/network/apiResponseSchema";
import { getToken } from "localStorage/auth";
import { getHandledErrorsFromRequestResult } from "shared/errors/errorHandling";
import { ErrorCodesType, generalErrorCodes } from "shared/errors/generalErrorCodes";

export type SortParameter =
  | { column: string; direction: "asc" | "desc" }[]
  | undefined;
export type RangeParameter = { from: number; to: number } | undefined;
export type FilterOperator =
  | "like"
  | "nlike"
  | "eq"
  | "neq"
  | "lt"
  | "lte"
  | "gt"
  | "gte"
  | "null"
  | "nnull";
export type FilterParameter =
  | {
      column: string;
      value: string | string[];
      operator: FilterOperator;
    }[]
  | undefined;

export const makeMutation = async (
  url: string,
  values?: unknown,
  withAuth: boolean = false,
  method: "POST" | "PATCH" | "DELETE" = "POST",
  errorCodes: ErrorCodesType = generalErrorCodes
) => {
  const token = getToken();
  const finalUrl = `${settings.apiUrl}${url}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token && withAuth) {
    headers["X-Auth-Token"] = token;
  }
  const res = await fetch(finalUrl, {
    method,
    headers,
    body: values ? JSON.stringify(values) : undefined,
  });
  const json = await res.json();
  const parsedResponse = ApiMutationResponseSchema.safeParse(json);
  if (parsedResponse.success === false) {
    throw new ResponseFormatError(parsedResponse, res);
  } else {
    if (parsedResponse.success && parsedResponse.data.success) {
      return parsedResponse.data;
    } else {
      const handledErrors = getHandledErrorsFromRequestResult(json, errorCodes);

      const errors: Record<string, string> = {};
      handledErrors.getMessagesWithPath()
          .forEach((item) => (errors[item.path.join(".")] = item.message));

      throw new StandardApiError(
          parsedResponse.data,
          res,
          errors,
          handledErrors.getMessagesWithoutPath(),
      );
    }
  }
};

export const makeQuery = async (
  url: string,
  withAuth: boolean = false,
  sort: SortParameter = undefined,
  range: RangeParameter = undefined,
  filter: FilterParameter = undefined
) => {
  const token = getToken();
  const headers: {
    "X-Auth-Token"?: string;
    range?: string;
  } = {};
  if (withAuth && token) {
    headers["X-Auth-Token"] = token;
  }
  if (range) {
    headers.range = `items=${range.from}-${range.to}`;
  }
  const queryParams = [];
  if (sort) {
    queryParams.push(
      `orderBy=${encodeURIComponent(
        sort.map((item) => `${item.column}[${item.direction}]`).join(",")
      )}`
    );
  }
  if (filter && filter.length) {
    queryParams.push(
      `filterBy=${encodeURIComponent(
        filter
          .map((item) => {
            if (Array.isArray(item.value)) {
              return item.value
                .map(
                  (subValue) => `(${item.column}[${item.operator}]${subValue})`
                )
                .join("or");
            }
            return `(${item.column}[${item.operator}]${item.value})`;
          })
          .join("and")
      )}`
    );
  }
  const res = await fetch(
    `${settings.apiUrl}${url}${queryParams ? `?${queryParams.join("&")}` : ""}`,
    {
      headers,
    }
  );

  const json = await res.json();
  const parsedResponse = ApiQueryResponseSchema.safeParse(json);
  if (parsedResponse.success === false) {
    throw new ResponseFormatError(parsedResponse, res);
  } else {
    if (parsedResponse.success && parsedResponse.data.success) {
      return parsedResponse.data;
    } else {
      throw new StandardApiError(parsedResponse.data, res, {}, []);
    }
  }
};

export type MutationResponse = {
  success: boolean;
};
